import { useContext, useEffect, useState } from "react";
import TenantContext from "../context/tenant/TenantContext";
import { haveValue, selectPreferredServiceFile } from "../helper/common";

// Latest source file recorded for a tenant + service
// (TenantServiceFile: KYC | BANK_SCORE | EXPERIAN).
// Returns the record (with signed `url`) or null.
//
// Results are cached per tenant+service in module scope, so a parent
// component can prefetch (enabled=true on mount) and the modal's own
// call resolves synchronously from cache on open — no visible delay.
//
// `enabled` was previously `isOpen`: pass `true` in always-mounted
// parents to prefetch, or the modal's `isOpen` to fetch lazily.
const serviceFileCache = new Map();

const getCacheKey = (tenantId, service, guarantor) =>
  `${tenantId}:${service}:${guarantor ? "g" : "t"}`;

const useServiceFile = (
  tenantId,
  service,
  enabled = true,
  guarantor = false
) => {
  const { fetchServiceFiles } = useContext(TenantContext);
  const cacheKey = haveValue(tenantId)
    ? getCacheKey(tenantId, service, guarantor)
    : null;
  const [file, setFile] = useState(() =>
    cacheKey && serviceFileCache.has(cacheKey)
      ? serviceFileCache.get(cacheKey)
      : null
  );

  useEffect(() => {
    if (
      !enabled ||
      !haveValue(tenantId) ||
      typeof fetchServiceFiles !== "function" ||
      !cacheKey
    )
      return;
    if (serviceFileCache.has(cacheKey)) {
      setFile(serviceFileCache.get(cacheKey));
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetchServiceFiles(
          tenantId,
          service,
          false,
          guarantor
        );
        const record =
          selectPreferredServiceFile(res?.data?.success_res?.serviceFiles) ??
          null;
        serviceFileCache.set(cacheKey, record);
        if (!cancelled) setFile(record);
      } catch {
        if (!cancelled) setFile(null);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, tenantId, service, guarantor]);

  return file;
};

export default useServiceFile;
